import { cloneElement } from 'react'
import type {
  AriaAttributes,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { Icon, type IconName } from './icons.js'
import type { I18n } from './i18n.js'

/**
 * Primitives — the token-styled building blocks every screen composes.
 *
 * House rules for every primitive in this file:
 *  - typed props, `className` passthrough, no inline styles (variants map to CSS classes);
 *  - reachable and operable from the keyboard, with the visible focus ring from
 *    `--op-focus-ring-width`/`--op-color-focus`;
 *  - colour, spacing and motion come from tokens only, so both themes work untouched.
 *
 * They are deliberately hook-free: the state that matters (open, loading, invalid, selected) is
 * owned by the surface and passed down, which keeps the primitives pure and testable without a DOM.
 */

/* Button ------------------------------------------------------------------- */

/**
 * `deep` is the second primary action (deep navy): Send next to Sign's signature blue. Two
 * primaries are told apart by icon and words as well as colour.
 */
export type ButtonVariant = 'primary' | 'deep' | 'secondary' | 'quiet' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly ref?: Ref<HTMLButtonElement>
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  /** Disables the button and shows a spinner; the label stays in place. */
  readonly loading?: boolean
  readonly leadingIcon?: ReactNode
  readonly trailingIcon?: ReactNode
  /** Square control for a single icon; pair it with `aria-label`. */
  readonly iconOnly?: boolean
  readonly fullWidth?: boolean
}

/**
 * The classes a `Button` renders, for an element that must look like a button but is a link (a
 * router `Link` or an `<a>`), so no screen copies class strings by hand.
 */
export function buttonClassName(
  options: {
    readonly variant?: ButtonVariant
    readonly size?: ButtonSize
    readonly iconOnly?: boolean
    readonly fullWidth?: boolean
    readonly className?: string | undefined
  } = {},
): string {
  return [
    'op-button',
    `op-button--${options.variant ?? 'secondary'}`,
    `op-button--${options.size ?? 'md'}`,
    options.iconOnly === true ? 'op-button--icon-only' : '',
    options.fullWidth === true ? 'op-button--full' : '',
    options.className,
  ]
    .filter(Boolean)
    .join(' ')
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  leadingIcon,
  trailingIcon,
  iconOnly = false,
  fullWidth = false,
  className,
  type = 'button',
  disabled = false,
  children,
  ...rest
}: ButtonProps) {
  const classes = buttonClassName({
    variant,
    size,
    iconOnly,
    fullWidth,
    className: [loading ? 'is-loading' : '', className].filter(Boolean).join(' '),
  })
  const spinner = <Icon name="spinner" className="op-button__spinner" />
  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      {...rest}
    >
      {iconOnly && loading ? spinner : null}
      {iconOnly && loading ? null : loading ? spinner : leadingIcon}
      {iconOnly && loading ? null : children}
      {iconOnly ? null : trailingIcon}
    </button>
  )
}

/* Card --------------------------------------------------------------------- */

export type CardVariant = 'plain' | 'raised' | 'inset'

export interface CardProps {
  readonly title?: string
  readonly description?: string
  readonly actions?: ReactNode
  readonly variant?: CardVariant
  readonly headingLevel?: 2 | 3 | 4
  readonly className?: string
  readonly children?: ReactNode
}

export function Card({
  title,
  description,
  actions,
  variant = 'plain',
  headingLevel = 2,
  className,
  children,
}: CardProps) {
  const Heading = `h${headingLevel}` as const
  const hasHeader = title !== undefined || description !== undefined || actions !== undefined
  return (
    <section className={['op-card', `op-card--${variant}`, className].filter(Boolean).join(' ')}>
      {hasHeader ? (
        <header className="op-card__header">
          <div className="op-card__heading">
            {title === undefined ? null : <Heading className="op-card__title">{title}</Heading>}
            {description === undefined ? null : (
              <p className="op-card__description">{description}</p>
            )}
          </div>
          {actions === undefined ? null : <div className="op-card__actions">{actions}</div>}
        </header>
      ) : null}
      {children}
    </section>
  )
}

/* EmptyState --------------------------------------------------------------- */

export interface EmptyStateProps {
  readonly title: string
  readonly description?: string
  readonly action?: ReactNode
  /** Usually `<Icon name="document" size={24} />`; decorative unless it carries meaning. */
  readonly icon?: ReactNode
  readonly className?: string
}

export function EmptyState({ title, description, action, icon, className }: EmptyStateProps) {
  return (
    <div className={['op-empty-state', className].filter(Boolean).join(' ')}>
      {icon === undefined ? null : <span className="op-empty-state__icon">{icon}</span>}
      <strong className="op-empty-state__title">{title}</strong>
      {description === undefined ? null : (
        <span className="op-empty-state__description">{description}</span>
      )}
      {action === undefined ? null : <div className="op-empty-state__action">{action}</div>}
    </div>
  )
}

/* Badge / StatusDot -------------------------------------------------------- */

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info'

export interface BadgeProps {
  readonly tone?: Tone
  readonly className?: string
  readonly children: ReactNode
  /** Test and state hooks (`data-lifecycle`, `data-testid`) pass through to the element. */
  readonly [data: `data-${string}`]: string | undefined
}

export function Badge({ tone = 'neutral', className, children, ...data }: BadgeProps) {
  return (
    <span
      {...data}
      className={['op-badge', `op-badge--${tone}`, className].filter(Boolean).join(' ')}
    >
      {children}
    </span>
  )
}

export interface StatusDotProps {
  readonly tone?: Tone
  /** Accessible name for the state; omit when the dot is decorative next to a label. */
  readonly label?: string
  readonly className?: string
}

export function StatusDot({ tone = 'neutral', label, className }: StatusDotProps) {
  const classes = ['op-status-dot', `op-status-dot--${tone}`, className].filter(Boolean).join(' ')
  if (label === undefined) return <span className={classes} aria-hidden={true} />
  return <span className={classes} role="img" aria-label={label} />
}

/* PageHeader --------------------------------------------------------------- */

export interface PageHeaderProps {
  readonly title: string
  readonly description?: string
  /** A short fact beside the title, such as a list's count. */
  readonly meta?: ReactNode
  readonly actions?: ReactNode
  readonly headingLevel?: 1 | 2
  readonly className?: string
}

export function PageHeader({
  title,
  description,
  meta,
  actions,
  headingLevel = 1,
  className,
}: PageHeaderProps) {
  const Heading = `h${headingLevel}` as const
  return (
    <header className={['op-page-header', className].filter(Boolean).join(' ')}>
      <div className="op-page-header__heading">
        <div className="op-page-header__title-row">
          <Heading className="op-page-header__title">{title}</Heading>
          {meta === undefined ? null : <span className="op-page-header__meta">{meta}</span>}
        </div>
        {description === undefined ? null : (
          <p className="op-page-header__description">{description}</p>
        )}
      </div>
      {actions === undefined ? null : <div className="op-page-header__actions">{actions}</div>}
    </header>
  )
}

/* Form controls and Field -------------------------------------------------- */

/** The props `Field` injects into its control. */
export interface FieldControlProps extends Pick<
  AriaAttributes,
  'aria-describedby' | 'aria-invalid' | 'aria-required'
> {
  readonly id?: string
  readonly required?: boolean
}

export interface FieldProps {
  readonly label: string
  /**
   * Id for the label/control pair. `Field` does not generate one: the primitives stay hook-free
   * (no `useId`), so the surface owns the id and can rely on it in tests.
   */
  readonly id: string
  readonly hint?: string
  readonly error?: string
  readonly required?: boolean
  readonly className?: string
  /** `Input`, `Select` or `Textarea`; `Field` wires id, description and required state into it. */
  readonly children: ReactElement<FieldControlProps>
}

export function Field({
  label,
  id,
  hint,
  error,
  required = false,
  className,
  children,
}: FieldProps) {
  const controlId = children.props.id ?? id
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint === undefined ? '' : hintId, error === undefined ? '' : errorId]
    .filter(Boolean)
    .join(' ')

  const injected = {
    id: controlId,
    ...(required ? { required: true, 'aria-required': true } : {}),
    ...(describedBy === '' ? {} : { 'aria-describedby': describedBy }),
    ...(error === undefined ? {} : { 'aria-invalid': true }),
  }

  return (
    <div
      className={['op-field', error === undefined ? '' : 'op-field--invalid', className]
        .filter(Boolean)
        .join(' ')}
    >
      <label className="op-field__label" htmlFor={controlId}>
        {label}
        {required ? (
          <span className="op-field__required" aria-hidden={true}>
            *
          </span>
        ) : null}
      </label>
      {hint === undefined ? null : (
        <p className="op-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {cloneElement(children, injected)}
      {error === undefined ? null : (
        <p className="op-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly invalid?: boolean
  readonly className?: string
}

export function Input({ invalid = false, className, ...rest }: InputProps) {
  return (
    <input
      className={['op-input', invalid ? 'op-input--invalid' : '', className]
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid ? true : undefined}
      {...rest}
    />
  )
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly invalid?: boolean
  readonly className?: string
}

export function Select({ invalid = false, className, children, ...rest }: SelectProps) {
  return (
    <select
      className={['op-select', invalid ? 'op-select--invalid' : '', className]
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid ? true : undefined}
      {...rest}
    >
      {children}
    </select>
  )
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly invalid?: boolean
  readonly className?: string
}

export function Textarea({ invalid = false, className, rows = 4, ...rest }: TextareaProps) {
  return (
    <textarea
      className={['op-textarea', invalid ? 'op-textarea--invalid' : '', className]
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid ? true : undefined}
      rows={rows}
      {...rest}
    />
  )
}

/* Checkbox / Radio --------------------------------------------------------- */

export interface ChoiceProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * The visible label. With it, the control and its text form one clickable `<label>`; without it,
   * the control stands alone and the surface labels it (an `id` with a `<label htmlFor>`, or
   * `aria-label`/`aria-labelledby`), as a list row's leading control does.
   */
  readonly label?: ReactNode
  /** A line under the label, announced as the control's description. Needs `id` and `label`. */
  readonly hint?: ReactNode
  readonly invalid?: boolean
  readonly className?: string
}

function choice(type: 'checkbox' | 'radio', props: ChoiceProps): ReactElement {
  const {
    label,
    hint,
    invalid = false,
    className,
    id,
    'aria-describedby': describedByProp,
    ...rest
  } = props
  const hintId = hint !== undefined && id !== undefined ? `${id}-hint` : undefined
  // A caller's own description joins the hint rather than replacing it.
  const describedBy = [hintId, describedByProp].filter(Boolean).join(' ')
  const control = (
    <input
      type={type}
      id={id}
      className={['op-check', `op-check--${type}`, invalid ? 'op-check--invalid' : '', className]
        .filter(Boolean)
        .join(' ')}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      {...rest}
    />
  )
  if (label === undefined) return control
  return (
    <label
      className={['op-check-field', rest.disabled === true ? 'op-check-field--disabled' : '']
        .filter(Boolean)
        .join(' ')}
    >
      {control}
      <span className="op-check-field__text">
        <span className="op-check-field__label">{label}</span>
        {hint === undefined ? null : (
          <span className="op-check-field__hint" id={hintId}>
            {hint}
          </span>
        )}
      </span>
    </label>
  )
}

/** A checkbox on the design tokens: light and dark, the shared focus ring, disabled and invalid. */
export function Checkbox(props: ChoiceProps): ReactElement {
  return choice('checkbox', props)
}

/** A radio button on the same tokens; group radios with one `name` inside a `<fieldset>`. */
export function Radio(props: ChoiceProps): ReactElement {
  return choice('radio', props)
}

/* List / ListRow ----------------------------------------------------------- */

export interface ListProps {
  readonly className?: string
  readonly children: ReactNode
}

export function List({ className, children }: ListProps) {
  return <ul className={['op-list', className].filter(Boolean).join(' ')}>{children}</ul>
}

export interface ListRowProps {
  readonly title: ReactNode
  readonly leading?: ReactNode
  readonly meta?: ReactNode
  readonly trailing?: ReactNode
  /** Renders the row as a button. */
  readonly onClick?: () => void
  /** Renders the row as a link. */
  readonly href?: string
  readonly className?: string
}

export function ListRow({
  title,
  leading,
  meta,
  trailing,
  onClick,
  href,
  className,
}: ListRowProps) {
  const interactive = href !== undefined || onClick !== undefined
  const classes = ['op-list-row', interactive ? 'op-list-row--interactive' : '', className]
    .filter(Boolean)
    .join(' ')
  const inner = (
    <>
      {leading === undefined ? null : <span className="op-list-row__leading">{leading}</span>}
      <span className="op-list-row__body">
        <span className="op-list-row__title">{title}</span>
        {meta === undefined ? null : <span className="op-list-row__meta">{meta}</span>}
      </span>
      {trailing === undefined ? null : <span className="op-list-row__trailing">{trailing}</span>}
    </>
  )
  if (href !== undefined) {
    return (
      <li className={classes}>
        <a className="op-list-row__control" href={href}>
          {inner}
        </a>
      </li>
    )
  }
  if (onClick !== undefined) {
    return (
      <li className={classes}>
        <button type="button" className="op-list-row__control" onClick={onClick}>
          {inner}
        </button>
      </li>
    )
  }
  return <li className={classes}>{inner}</li>
}

/* Callout ------------------------------------------------------------------ */

export type CalloutTone = 'info' | 'success' | 'warning' | 'danger'

const CALLOUT_ICON: Record<CalloutTone, IconName> = {
  info: 'info',
  success: 'check',
  warning: 'warning',
  danger: 'error',
}

const CALLOUT_ROLE: Record<CalloutTone, 'status' | 'alert'> = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  danger: 'alert',
}

export interface CalloutProps {
  readonly tone?: CalloutTone
  readonly title?: string
  readonly children?: ReactNode
  /** Replaces the tone's default icon. */
  readonly icon?: ReactNode
  /** Overrides the default live-region role (`status`, `alert` for warning/danger). */
  readonly role?: 'status' | 'alert' | 'note'
  readonly className?: string
}

export function Callout({ tone = 'info', title, children, icon, role, className }: CalloutProps) {
  return (
    <div
      className={['op-callout', `op-callout--${tone}`, className].filter(Boolean).join(' ')}
      role={role ?? CALLOUT_ROLE[tone]}
    >
      <span className="op-callout__icon">{icon ?? <Icon name={CALLOUT_ICON[tone]} />}</span>
      <div className="op-callout__body">
        {title === undefined ? null : <p className="op-callout__title">{title}</p>}
        {children === undefined ? null : <div className="op-callout__text">{children}</div>}
      </div>
    </div>
  )
}

/* Stat --------------------------------------------------------------------- */

export interface StatProps {
  readonly label: string
  readonly value: ReactNode
  /** A qualifier for the figure — a scope, a window, an estimate. A node, so a surface can keep
   * its own test hook (or a badge) inside the line rather than beside the card. */
  readonly hint?: ReactNode
  readonly className?: string
}

export function Stat({ label, value, hint, className }: StatProps) {
  return (
    <div className={['op-stat', className].filter(Boolean).join(' ')}>
      <span className="op-stat__label">{label}</span>
      <span className="op-stat__value">{value}</span>
      {hint === undefined ? null : <span className="op-stat__hint">{hint}</span>}
    </div>
  )
}

/* ErrorCode ---------------------------------------------------------------- */

export interface ErrorCodeProps {
  /** The already-translated line, usually `i18n.t('error.code_line', { code })`. */
  readonly text: string
  readonly className?: string
}

/**
 * A failure's support code, on its own muted line under the sentence that says what happened
 * (ADD-004/OPS-05, sweep rank 6). The sentence stays readable and support still gets the code, so a
 * surface never puts the code inside the sentence itself.
 */
export function ErrorCode({ text, className }: ErrorCodeProps): ReactElement {
  return <span className={['op-error-code', className].filter(Boolean).join(' ')}>{text}</span>
}

export interface CodeMessage {
  /** The already-translated sentence. */
  readonly message: string
  /** The support code, rendered on its own muted line under the sentence. */
  readonly code?: string | undefined
}

export interface MessageWithCodeProps extends CodeMessage {
  readonly i18n: I18n
}

/**
 * A sentence with an optional support code under it, for state that holds `{ message, code }`
 * rather than rendering straight into JSX (sweep rank 6). A notice that has no code renders as the
 * sentence alone.
 */
export function MessageWithCode({ i18n, message, code }: MessageWithCodeProps): ReactElement {
  return (
    <>
      {message}
      {code === undefined ? null : <ErrorCode text={i18n.t('error.code_line', { code })} />}
    </>
  )
}

/* Toolbar ------------------------------------------------------------------ */

export interface ToolbarProps {
  readonly className?: string
  /** Names the group of controls, e.g. "Agreement filters". */
  readonly label?: string
  readonly children: ReactNode
}

export function Toolbar({ className, label, children }: ToolbarProps) {
  return (
    <div
      className={['op-toolbar', className].filter(Boolean).join(' ')}
      role={label === undefined ? undefined : 'group'}
      aria-label={label}
    >
      {children}
    </div>
  )
}

/* Skeleton ----------------------------------------------------------------- */

export type SkeletonSize = 'text' | 'title' | 'block' | 'circle'
export type SkeletonWidth = 'xs' | 'sm' | 'md' | 'lg' | 'full'

export interface SkeletonProps {
  readonly size?: SkeletonSize
  readonly width?: SkeletonWidth
  /** Number of stacked bars. */
  readonly lines?: number
  /** Announce a loading region; without it the placeholder is hidden from assistive tech. */
  readonly label?: string
  readonly className?: string
}

export function Skeleton({
  size = 'text',
  width = 'md',
  lines = 1,
  label,
  className,
}: SkeletonProps) {
  const classes = ['op-skeleton', `op-skeleton--${size}`, `op-skeleton--w-${width}`, className]
    .filter(Boolean)
    .join(' ')
  const count = Math.max(1, Math.trunc(lines))
  const bars = Array.from({ length: count }, (_, index) => <span key={index} className={classes} />)
  if (label === undefined) {
    return (
      <span className="op-skeleton-group" aria-hidden={true}>
        {bars}
      </span>
    )
  }
  return (
    // The label is text, not only an aria-label, so the status never relies on the bars alone.
    <span className="op-skeleton-group" role="status">
      <span className="op-visually-hidden">{label}</span>
      {bars}
    </span>
  )
}

/* Sheet -------------------------------------------------------------------- */

export interface SheetProps {
  /** Keep the close control outside the scrollable content on long previews. */
  readonly stickyHeader?: boolean
  readonly open: boolean
  readonly title: string
  readonly onClose: () => void
  /**
   * The accessible name of the close button in the sheet's header, usually
   * `i18n.t('common.close')`. Without it the sheet has no close button, so its own actions must
   * include a way out (Cancel); Escape and the backdrop close it either way.
   */
  readonly closeLabel?: string
  readonly className?: string
  readonly children: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** The sheet's tabbable controls, in order; hidden ones (`hidden`, `display: none`) are skipped. */
function tabbable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => !element.closest('[hidden]') && element.getClientRects().length > 0,
  )
}

/** The latest focus move in the document, so a sheet whose control autofocused knows its opener. */
let lastFocusMove: { readonly to: HTMLElement; readonly from: HTMLElement } | null = null
if (typeof document !== 'undefined') {
  document.addEventListener(
    'focusin',
    (event) => {
      const { target, relatedTarget } = event
      lastFocusMove =
        target instanceof HTMLElement && relatedTarget instanceof HTMLElement
          ? { to: target, from: relatedTarget }
          : null
    },
    true,
  )
}

/**
 * What a sheet returns focus to on close: whatever had focus as it opened, or, when a control in the
 * sheet autofocused and so already took focus from the opener, where that last focus move came from.
 */
export function sheetOpener<T>(
  focused: T | null,
  inSheet: (node: T) => boolean,
  lastMove: { readonly to: T; readonly from: T } | null,
): T | null {
  if (focused === null || !inSheet(focused)) return focused
  return lastMove !== null && lastMove.to === focused && !inSheet(lastMove.from)
    ? lastMove.from
    : null
}

/**
 * Focus for a modal sheet, as a ref callback so the primitive stays hook-free. It is one stable
 * function, so React calls it only when the dialog mounts and runs its cleanup when it unmounts:
 * focus moves into the sheet (unless a control in it already took it, e.g. `autoFocus`) and goes
 * back to whatever opened it once it closes.
 */
function sheetFocus(dialog: HTMLDivElement | null): (() => void) | undefined {
  if (dialog === null || typeof document === 'undefined') return undefined
  const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null
  const opener = sheetOpener(focused, (node) => dialog.contains(node), lastFocusMove)
  if (focused !== null && !dialog.contains(focused)) focused.blur()
  window.dispatchEvent(new Event('signbento:modal-open'))
  if (!dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true })
  return () => {
    const active = document.activeElement
    // Only when focus would otherwise be lost: an action that moved it on (a new page, another
    // sheet) keeps it where it went.
    const lost = active === null || active === document.body || dialog.contains(active)
    if (lost && opener !== null && opener.isConnected) opener.focus({ preventScroll: true })
  }
}

/** Escape closes; Tab and Shift+Tab wrap inside the sheet so focus never reaches the page behind. */
function handleSheetKey(event: KeyboardEvent<HTMLDivElement>, onClose: () => void): void {
  if (event.key === 'Escape') {
    event.stopPropagation()
    onClose()
    return
  }
  if (event.key !== 'Tab') return
  const dialog = event.currentTarget
  const controls = tabbable(dialog)
  const first = controls[0]
  const last = controls[controls.length - 1]
  if (first === undefined || last === undefined) {
    event.preventDefault()
    return
  }
  const active = document.activeElement
  if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

export function Sheet({
  open,
  title,
  onClose,
  closeLabel,
  className,
  children,
  stickyHeader = false,
}: SheetProps) {
  if (!open) return null
  return (
    <div className="op-sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={sheetFocus}
        className={['op-sheet', stickyHeader ? 'op-sheet--scroll-body' : '', className]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal={true}
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => handleSheetKey(event, onClose)}
      >
        <div className="op-sheet__header">
          <h2 className="op-sheet__title">{title}</h2>
          {closeLabel === undefined ? null : (
            <Button
              variant="quiet"
              size="sm"
              iconOnly
              className="op-sheet__close"
              aria-label={closeLabel}
              data-testid="sheet-close"
              onClick={onClose}
            >
              <Icon name="close" />
            </Button>
          )}
        </div>
        {stickyHeader ? <div className="op-sheet__body">{children}</div> : children}
      </div>
    </div>
  )
}

/* Tabs --------------------------------------------------------------------- */

export interface TabItem {
  readonly id: string
  readonly label: ReactNode
  readonly disabled?: boolean
  readonly icon?: IconName
  /** `data-testid` for the tab control, so a surface keeps the selectors its tests already use. */
  readonly testId?: string
}

export interface TabsProps {
  readonly items: readonly TabItem[]
  /** The selected tab's id; the surface owns the state. */
  readonly value: string
  readonly onChange: (id: string) => void
  /** `segmented` is a quiet track for in-place panels; `underline` marks the current section. */
  readonly variant?: 'segmented' | 'underline'
  readonly size?: 'sm' | 'md'
  /** Segments share the track's width instead of hugging their labels. */
  readonly fullWidth?: boolean
  /** The tablist's accessible name. */
  readonly 'aria-label': string
  /** Prefix for the tab and panel ids; without it the tabs carry no ids or `aria-controls`. */
  readonly idPrefix?: string
  readonly className?: string
  /** Test and state hooks (`data-testid`, `data-*`) pass through to the tablist. */
  readonly [data: `data-${string}`]: string | undefined
}

/**
 * The panel props for a tab: `role`, `id` and `aria-labelledby` matching the tab's own `id` and
 * `aria-controls`, so the pair can never be wired inconsistently. Spread onto the panel element.
 */
export function tabPanelProps(
  idPrefix: string,
  id: string,
): { role: 'tabpanel'; id: string; 'aria-labelledby': string } {
  return {
    role: 'tabpanel',
    id: `${idPrefix}-panel-${id}`,
    'aria-labelledby': `${idPrefix}-tab-${id}`,
  }
}

/** The next enabled tab `step` away, wrapping and skipping disabled ones; `-1` if none is enabled. */
function stepEnabled(items: readonly TabItem[], from: number, step: number): number {
  const count = items.length
  let index = from
  for (let hop = 0; hop < count; hop += 1) {
    index = (index + step + count) % count
    if (items[index]?.disabled !== true) return index
  }
  return -1
}

/** The first or last enabled tab; `-1` when every tab is disabled. */
function edgeEnabled(items: readonly TabItem[], fromEnd: boolean): number {
  const count = items.length
  for (let offset = 0; offset < count; offset += 1) {
    const index = fromEnd ? count - 1 - offset : offset
    if (items[index]?.disabled !== true) return index
  }
  return -1
}

/** Right-to-left, read from the element's own `[dir]` scope and falling back to the document. */
function isRtl(element: EventTarget | null): boolean {
  if (typeof Element !== 'undefined' && element instanceof Element) {
    const scoped = element.closest('[dir]')?.getAttribute('dir')
    if (scoped === 'rtl' || scoped === 'ltr') return scoped === 'rtl'
  }
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl'
}

/** Automatic activation: the arrow/Home/End keys select the tab they move to. */
function handleTabKey(
  event: KeyboardEvent<HTMLDivElement>,
  items: readonly TabItem[],
  value: string,
  onChange: (id: string) => void,
): void {
  const from = items.findIndex((item) => item.id === value)
  const rtl = isRtl(event.currentTarget)
  let target = -1
  switch (event.key) {
    case 'ArrowRight':
      target = stepEnabled(items, from, rtl ? -1 : 1)
      break
    case 'ArrowLeft':
      target = stepEnabled(items, from, rtl ? 1 : -1)
      break
    case 'Home':
      target = edgeEnabled(items, false)
      break
    case 'End':
      target = edgeEnabled(items, true)
      break
    default:
      return
  }
  const item = items[target]
  if (item === undefined) return
  event.preventDefault()
  // The selection follows focus, so move focus to the tab being selected as well as selecting it.
  const owner = event.currentTarget
  if (typeof HTMLElement !== 'undefined' && owner instanceof HTMLElement) {
    owner.querySelectorAll<HTMLElement>('[role="tab"]')[target]?.focus()
  }
  onChange(item.id)
}

export function Tabs({
  items,
  value,
  onChange,
  variant = 'segmented',
  size = 'md',
  fullWidth = false,
  'aria-label': ariaLabel,
  idPrefix,
  className,
  ...data
}: TabsProps) {
  const classes = [
    'op-tabs',
    `op-tabs--${variant}`,
    size === 'md' ? '' : `op-tabs--${size}`,
    fullWidth ? 'op-tabs--full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  // When `value` names no item, or a disabled one that can't take focus, the roving tabIndex would
  // leave the tablist with no reachable tab: the first enabled tab takes the focus stop instead.
  const focusableId = items.some((item) => item.id === value && item.disabled !== true)
    ? value
    : items.find((item) => item.disabled !== true)?.id
  return (
    <div
      {...data}
      className={classes}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={(event) => handleTabKey(event, items, value, onChange)}
    >
      {items.map((item) => {
        const selected = item.id === value
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={idPrefix === undefined ? undefined : `${idPrefix}-tab-${item.id}`}
            className="op-tabs__tab"
            aria-selected={selected}
            // Only the selected tab's panel is in the document: the callers render that one alone, so
            // pointing the other tabs at ids that do not exist would break the a11y check.
            aria-controls={
              selected && idPrefix !== undefined ? `${idPrefix}-panel-${item.id}` : undefined
            }
            aria-current={variant === 'underline' && selected ? 'page' : undefined}
            tabIndex={item.id === focusableId ? 0 : -1}
            disabled={item.disabled === true}
            data-testid={item.testId}
            onClick={() => {
              if (item.disabled !== true) onChange(item.id)
            }}
          >
            {item.icon === undefined ? null : <Icon name={item.icon} />}
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
